/**
 * What the side panel remembers between sessions (docs/24 P9).
 *
 * The open flag and the conversation travel together, deliberately. Persisting *open*
 * without persisting *which conversation* produces the worst greeting the panel can give: it
 * opens itself, unasked, and is empty — while the thread sits on the server. To someone
 * non-technical that reads as "my chat is gone", and it is the one case where the panel
 * appears without being asked for.
 *
 * The workspace rides along for the same reason the provider clears itself on a switch: a
 * thread belongs to one workspace, and rehydrating A's conversation into B would be the very
 * leak that reset exists to prevent. A stored entry from another workspace is ignored, not
 * repaired.
 */

const KEY = 'fo-assistant-panel';

export interface PanelState {
  open: boolean;
  /** The conversation to restore, or null for a fresh one. */
  threadId: string | null;
  /** The workspace `threadId` belongs to. */
  workspaceId: string | null;
}

const CLOSED: PanelState = { open: false, threadId: null, workspaceId: null };

export function readPanelState(): PanelState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return CLOSED;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return CLOSED;
    const { open, threadId, workspaceId } = parsed as Record<string, unknown>;
    return {
      open: open === true,
      threadId: typeof threadId === 'string' && threadId ? threadId : null,
      workspaceId: typeof workspaceId === 'string' && workspaceId ? workspaceId : null,
    };
  } catch {
    // Unreadable storage (private browsing) or a value written by an older shape: the panel
    // starts closed, which is the state that asks nothing of the user.
    return CLOSED;
  }
}

export function writePanelState(state: PanelState): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // ignore storage failures (e.g. private browsing) — mirrors the sidebar-collapse pattern
  }
}
